import { jsPDF } from "jspdf";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

// Funktion zum Erstellen und Herunterladen des Kursplans als PDF
export const exportSchedulePDF = async (schedule, students) => {
  console.log("Exportiere PDF. Übergebene schedule:", schedule);
  console.log("Exportiere PDF. Übergebene students:", students);

  // Fetch coaches data
  let coaches = [];
  try {
    const response = await axios.get(`${API_URL}/api/coaches`);
    coaches = response.data;
    console.log("Coaches für PDF:", coaches);
  } catch (error) {
    console.error("Fehler beim Abrufen der Trainerdaten:", error);
    toast.error("Fehler beim Abrufen der Trainerdaten für PDF-Export");
    return;
  }

  // Create PDF document in landscape mode
  const doc = new jsPDF("landscape");

  const days = [
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
  ];

  // Helper function to find coach by ID or name
  const findCoachByIdOrName = (val) => {
    if (!val) return null;
    const v = String(val).trim();
    const byId = coaches.find((c) => String(c._id) === v);
    if (byId) return byId;
    return coaches.find((c) => `${c.firstName} ${c.lastName}` === v) || null;
  };

  // Helper to get color indicator for children
  const getChildIndicator = (student) => {
    if (student.adult) return "";

    const groupIndicatorMap = {
      "Kinderland": " (K)",
      "Rot": " (R)",
      "Grün": " (G)",
      "Orange": " (O)",
      "Gelb Team": " (YT)",
      "Gelb Hobby": " (YH)"
    };

    return groupIndicatorMap[student.trainigGroup] || " (-)";
  };

  // Helper function to generate week view for all coaches with auto-pagination
  const generateWeekView = (doc, title, filterStudents, pageIndex, hourRange = null) => {
    // Filter students based on criteria
    const filteredStudents = filterStudents(students);

    // Collect all hours where at least one coach has availability
    const allHours = new Set();
    coaches.forEach((coach) => {
      const coachAvailableTimes = Array.isArray(coach.availableTimes)
        ? coach.availableTimes
        : [];
      coachAvailableTimes.forEach((time) => {
        const [day, hourStr] = String(time).split(" ");
        const hour = parseInt(hourStr, 10);
        if (!isNaN(hour)) {
          allHours.add(hour);
        }
      });
    });

    let sortedHours = Array.from(allHours).sort((a, b) => a - b);

    // Apply hour range filter if provided
    if (hourRange) {
      sortedHours = sortedHours.filter(h => h >= hourRange.min && h <= hourRange.max);
    }

    if (sortedHours.length === 0) {
      doc.setFontSize(12);
      doc.text("Keine verfügbaren Zeiten.", 10, 30);
      return;
    }

    // Pre-calculate which hours have students (for dynamic row height)
    const hoursWithStudents = new Set();
    sortedHours.forEach((hour) => {
      const hasStudents = days.some((day) => {
        return filteredStudents.some(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );
      });
      if (hasStudents) {
        hoursWithStudents.add(hour);
      }
    });

    // Layout parameters
    const startX = 50;
    const startY = 30;
    const cellWidth = 40;
    const cellHeight = 20; // Default height for rows with students
    const emptyRowHeight = 8; // Reduced height for completely empty rows
    const minHeightPerCoach = 15; // Minimum height per coach sub-cell to fit all student names

    // Calculate dynamic row height based on content
    const getRowHeight = (hour) => {
      if (!hoursWithStudents.has(hour)) {
        return emptyRowHeight;
      }

      let maxCoachesInAnyCell = 1;

      // Find the maximum number of coaches sharing any cell at this hour
      days.forEach((day) => {
        const studentsInCell = filteredStudents.filter(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );

        if (studentsInCell.length === 0) return;

        // Group by coach to count how many coaches share this cell
        const coachSet = new Set();
        studentsInCell.forEach((s) => {
          const coach = findCoachByIdOrName(s.coach);
          const coachKey = coach ? String(coach._id) : "nocoach";
          coachSet.add(coachKey);
        });

        maxCoachesInAnyCell = Math.max(maxCoachesInAnyCell, coachSet.size);
      });

      // Return height based on max coaches: ensure each coach has enough space
      return Math.max(cellHeight, maxCoachesInAnyCell * minHeightPerCoach);
    };

    // Calculate cumulative Y positions for each row
    const rowYPositions = [];
    let cumulativeY = startY;
    sortedHours.forEach((hour) => {
      rowYPositions.push(cumulativeY);
      cumulativeY += getRowHeight(hour);
    });
    const totalHeight = cumulativeY - startY;

    // Dynamic pagination: Check if content fits on one page
    const pageHeight = doc.internal.pageSize.height;
    const maxContentHeight = pageHeight - startY - 15; // Reserve space for margins

    // Split hours into chunks that fit on pages
    const hourChunks = [];
    let currentChunk = [];
    let currentChunkHeight = 0;

    sortedHours.forEach((hour, idx) => {
      const hourHeight = getRowHeight(hour);

      if (currentChunkHeight + hourHeight > maxContentHeight && currentChunk.length > 0) {
        // Current chunk full, start new chunk
        hourChunks.push(currentChunk);
        currentChunk = [hour];
        currentChunkHeight = hourHeight;
      } else {
        currentChunk.push(hour);
        currentChunkHeight += hourHeight;
      }
    });

    // Add remaining chunk
    if (currentChunk.length > 0) {
      hourChunks.push(currentChunk);
    }

    // Render each chunk on a separate page
    hourChunks.forEach((hourChunk, chunkIndex) => {
      // Add new page for each chunk (except skip if first chunk and pageIndex = 0)
      if (pageIndex > 0 || chunkIndex > 0) {
        doc.addPage();
      }

      // Title with page indicator if multiple chunks
      doc.setFontSize(16);
      const chunkTitle = hourChunks.length > 1
        ? `${title} - Teil ${chunkIndex + 1}/${hourChunks.length}`
        : title;
      doc.text(chunkTitle, 10, 10);

      // Recalculate positions for this chunk
      const chunkRowYPositions = [];
      let chunkCumulativeY = startY;
      hourChunk.forEach((hour) => {
        chunkRowYPositions.push(chunkCumulativeY);
        chunkCumulativeY += getRowHeight(hour);
      });
      const chunkTotalHeight = chunkCumulativeY - startY;

      // Draw header (days)
      doc.setFontSize(10);
      days.forEach((day, index) => {
        const xPosition = startX + cellWidth * index;
        doc.text(day, xPosition + 2, startY - 5);
        // Draw vertical line for column
        doc.line(
          xPosition,
          startY,
          xPosition,
          startY + chunkTotalHeight
        );
      });

      // Draw right border
      doc.line(
        startX + cellWidth * days.length,
        startY,
        startX + cellWidth * days.length,
        startY + chunkTotalHeight
      );

      // Draw rows for each hour in this chunk
      hourChunk.forEach((hour, rowIndex) => {
        const yPosition = chunkRowYPositions[rowIndex];
        const currentRowHeight = getRowHeight(hour);

        // Draw hour label on the left (smaller font for empty rows)
      doc.setFontSize(hoursWithStudents.has(hour) ? 10 : 8);
      doc.text(`${hour} bis ${hour + 1} Uhr`, startX - 35, yPosition + currentRowHeight / 2);

      // Draw horizontal line
      doc.line(
        startX,
        yPosition,
        startX + cellWidth * days.length,
        yPosition
      );

      // Skip cell filling for empty rows (no students at this hour)
      if (!hoursWithStudents.has(hour)) return;

      // Fill cells with students
      days.forEach((day, colIndex) => {
        const xPosition = startX + cellWidth * colIndex;

        // Find students in this cell
        const studentsInCell = filteredStudents.filter(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );

        if (studentsInCell.length === 0) return;

        // Group students by coach
        const studentsByCoach = {};
        studentsInCell.forEach((s) => {
          const coach = findCoachByIdOrName(s.coach);
          const coachKey = coach ? String(coach._id) : "nocoach";
          const coachName = coach ? `${coach.firstName.charAt(0)}.${coach.lastName}` : "?";

          if (!studentsByCoach[coachKey]) {
            studentsByCoach[coachKey] = {
              coachName: coachName,
              students: []
            };
          }
          studentsByCoach[coachKey].students.push(s);
        });

        const coachGroups = Object.values(studentsByCoach);
        const numCoaches = coachGroups.length;
        const subCellHeight = currentRowHeight / numCoaches;

        // Draw each coach's section
        coachGroups.forEach((group, coachIdx) => {
          const subYPosition = yPosition + (subCellHeight * coachIdx);

          // Draw horizontal divider line between coaches (if multiple)
          if (coachIdx > 0) {
            doc.setDrawColor(200, 200, 200); // Light gray
            doc.line(
              xPosition,
              subYPosition,
              xPosition + cellWidth,
              subYPosition
            );
            doc.setDrawColor(0, 0, 0); // Reset to black
          }

          // Draw coach name at top of sub-cell
          doc.setFontSize(7);
          doc.setFont(undefined, 'bold');
          doc.text(group.coachName, xPosition + 2, subYPosition + 4);
          doc.setFont(undefined, 'normal');

          // Draw student names below coach name
          const studentNames = group.students
            .map((s) => `${s.firstName} ${s.lastName}${getChildIndicator(s)}`)
            .join(", ");

          doc.setFontSize(6);
          const wrappedText = doc.splitTextToSize(
            studentNames,
            cellWidth - 4
          );

          wrappedText.forEach((line, lineIndex) => {
            const textY = subYPosition + 7 + lineIndex * 2.5;
            if (textY < subYPosition + subCellHeight - 1) {
              doc.text(line, xPosition + 2, textY);
            }
          });
        });
        });
      });

      // Draw bottom border for this chunk
      doc.line(
        startX,
        startY + chunkTotalHeight,
        startX + cellWidth * days.length,
        startY + chunkTotalHeight
      );
    }); // End of hourChunks.forEach
  };

  // Page 1a: All courses (all students) - Hours 10:00-15:00
  generateWeekView(
    doc,
    "Kursplan - Alle Kurse (10:00-15:00)",
    (students) => students,
    0,
    { min: 10, max: 15 }
  );

  // Page 1b: All courses (all students) - Hours 16:00-21:00
  generateWeekView(
    doc,
    "Kursplan - Alle Kurse (16:00-21:00)",
    (students) => students,
    1,
    { min: 16, max: 21 }
  );

  // Page 2: Children courses only - All hours
  generateWeekView(
    doc,
    "Kursplan - Kinder",
    (students) => students.filter((s) => !s.adult),
    2,
    null
  );

  // Page 3: Adults courses only - All hours
  generateWeekView(
    doc,
    "Kursplan - Erwachsene",
    (students) => students.filter((s) => s.adult),
    3,
    null
  );

  // Generate one page per coach (starting after the 3 overview pages)
  coaches.forEach((coach, coachIndex) => {
    // Add new page for each coach (always, since we have 3 overview pages before)
    doc.addPage();

    // Title with coach name
    doc.setFontSize(16);
    doc.text(`Kursplan - ${coach.firstName} ${coach.lastName}`, 10, 10);

    // Get available times for this coach
    const coachAvailableTimes = Array.isArray(coach.availableTimes)
      ? coach.availableTimes
      : [];

    // Extract unique days and hours for this coach
    const coachDays = [];
    const coachHours = new Set();

    coachAvailableTimes.forEach((time) => {
      const [day, hourStr] = String(time).split(" ");
      const hour = parseInt(hourStr, 10);
      if (day && !isNaN(hour)) {
        if (!coachDays.includes(day) && days.includes(day)) {
          coachDays.push(day);
        }
        coachHours.add(hour);
      }
    });

    // Sort days by week order and hours numerically
    coachDays.sort((a, b) => days.indexOf(a) - days.indexOf(b));
    const sortedHours = Array.from(coachHours).sort((a, b) => a - b);

    // If no available times, show message
    if (coachDays.length === 0 || sortedHours.length === 0) {
      doc.setFontSize(12);
      doc.text("Keine verfügbaren Zeiten für diesen Trainer.", 10, 30);
      return;
    }

    // Filter students assigned to this coach
    const coachStudents = students.filter((student) => {
      if (!student.coach) return false;
      const studentCoach = findCoachByIdOrName(student.coach);
      return studentCoach && String(studentCoach._id) === String(coach._id);
    });

    console.log(
      `Coach ${coach.firstName} ${coach.lastName}: ${coachStudents.length} students`
    );

    // Pre-calculate which hours have students (for dynamic row height)
    const hoursWithStudents = new Set();
    sortedHours.forEach((hour) => {
      const hasStudents = coachDays.some((day) => {
        return coachStudents.some(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );
      });
      if (hasStudents) {
        hoursWithStudents.add(hour);
      }
    });

    // Layout parameters
    const startX = 50;
    const startY = 30;
    const cellWidth = 40;
    const cellHeight = 20; // Default height for rows with students
    const emptyRowHeight = 8; // Reduced height for completely empty rows
    const minHeightPerCoach = 15; // Minimum height per coach sub-cell to fit all student names

    // Calculate dynamic row height based on content (for individual coach pages)
    const getRowHeight = (hour) => {
      if (!hoursWithStudents.has(hour)) {
        return emptyRowHeight;
      }

      let maxCoachesInAnyCell = 1;

      // Find the maximum number of coaches sharing any cell at this hour
      coachDays.forEach((day) => {
        const studentsInCell = coachStudents.filter(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );

        if (studentsInCell.length === 0) return;

        // Group by coach to count how many coaches share this cell
        const coachSet = new Set();
        studentsInCell.forEach((s) => {
          const coach = findCoachByIdOrName(s.coach);
          const coachKey = coach ? String(coach._id) : "nocoach";
          coachSet.add(coachKey);
        });

        maxCoachesInAnyCell = Math.max(maxCoachesInAnyCell, coachSet.size);
      });

      // Return height based on max coaches: ensure each coach has enough space
      return Math.max(cellHeight, maxCoachesInAnyCell * minHeightPerCoach);
    };

    // Calculate cumulative Y positions for each row
    const rowYPositions = [];
    let cumulativeY = startY;
    sortedHours.forEach((hour) => {
      rowYPositions.push(cumulativeY);
      cumulativeY += getRowHeight(hour);
    });
    const totalHeight = cumulativeY - startY;

    // Dynamic pagination for coach pages
    const pageHeight = doc.internal.pageSize.height;
    const maxContentHeight = pageHeight - startY - 15;

    // Split hours into chunks
    const hourChunks = [];
    let currentChunk = [];
    let currentChunkHeight = 0;

    sortedHours.forEach((hour) => {
      const hourHeight = getRowHeight(hour);

      if (currentChunkHeight + hourHeight > maxContentHeight && currentChunk.length > 0) {
        hourChunks.push(currentChunk);
        currentChunk = [hour];
        currentChunkHeight = hourHeight;
      } else {
        currentChunk.push(hour);
        currentChunkHeight += hourHeight;
      }
    });

    if (currentChunk.length > 0) {
      hourChunks.push(currentChunk);
    }

    // Render each chunk
    hourChunks.forEach((hourChunk, chunkIndex) => {
      // Add new page for additional chunks
      if (chunkIndex > 0) {
        doc.addPage();

        // Repeat title for continuation pages
        doc.setFontSize(16);
        const chunkTitle = hourChunks.length > 1
          ? `Kursplan - ${coach.firstName} ${coach.lastName} - Teil ${chunkIndex + 1}/${hourChunks.length}`
          : `Kursplan - ${coach.firstName} ${coach.lastName}`;
        doc.text(chunkTitle, 10, 10);
      }

      // Recalculate positions for this chunk
      const chunkRowYPositions = [];
      let chunkCumulativeY = startY;
      hourChunk.forEach((hour) => {
        chunkRowYPositions.push(chunkCumulativeY);
        chunkCumulativeY += getRowHeight(hour);
      });
      const chunkTotalHeight = chunkCumulativeY - startY;

      // Draw header (days)
      doc.setFontSize(10);
      coachDays.forEach((day, index) => {
        const xPosition = startX + cellWidth * index;
        doc.text(day, xPosition + 2, startY - 5);
        // Draw vertical line for column
        doc.line(
          xPosition,
          startY,
          xPosition,
          startY + chunkTotalHeight
        );
      });

      // Draw right border
      doc.line(
        startX + cellWidth * coachDays.length,
        startY,
        startX + cellWidth * coachDays.length,
        startY + chunkTotalHeight
      );

      // Draw rows for each hour in this chunk
      hourChunk.forEach((hour, rowIndex) => {
        const yPosition = chunkRowYPositions[rowIndex];
        const currentRowHeight = getRowHeight(hour);

        // Draw hour label on the left (smaller font for empty rows)
      doc.setFontSize(hoursWithStudents.has(hour) ? 10 : 8);
      doc.text(`${hour} bis ${hour + 1} Uhr`, startX - 35, yPosition + currentRowHeight / 2);

      // Draw horizontal line
      doc.line(
        startX,
        yPosition,
        startX + cellWidth * coachDays.length,
        yPosition
      );

      // Skip cell filling for empty rows (no students at this hour)
      if (!hoursWithStudents.has(hour)) return;

      // Fill cells with students
      coachDays.forEach((day, colIndex) => {
        const xPosition = startX + cellWidth * colIndex;

        // Find students in this cell
        const studentsInCell = coachStudents.filter(
          (student) => student.day === day && parseInt(student.hour, 10) === hour
        );

        // Generate student names with indicators
        const studentNames = studentsInCell
          .map((s) => `${s.firstName} ${s.lastName}${getChildIndicator(s)}`)
          .join("\n");

        // Wrap text to fit cell
        doc.setFontSize(8);
        const wrappedText = doc.splitTextToSize(
          studentNames || "",
          cellWidth - 4
        );

        // Draw text inside cell
        wrappedText.forEach((line, lineIndex) => {
          const textY = yPosition + 5 + lineIndex * 3;
          if (textY < yPosition + currentRowHeight - 2) {
            doc.text(line, xPosition + 2, textY);
          }
        });
        });
      });

      // Draw bottom border for this chunk
      doc.line(
        startX,
        startY + chunkTotalHeight,
        startX + cellWidth * coachDays.length,
        startY + chunkTotalHeight
      );
    }); // End of hourChunks.forEach
  });

  // Save PDF
  const date = new Date()
    .toISOString()
    .replace("T", "_")
    .slice(0, 16)
    .replace(/:/g, "-");

  doc.save(`Kursplan_${date}.pdf`);
};
